/** API paths, UI config, and mappers for admin rider pages (Section 17-style config). */

import { buildPersonLegalName } from "../../../globals/utils/nameUtils";
import { buildPrincipalListPatchExtras } from "./riderHelpers";

export const RIDER_BOND_KIND = Object.freeze({
  probate: "probate",
  guardianship: "guardianship",
  trustee: "trustee",
  va: "va",
});

/** Standard endorsement types (non-VA). */
export const TYPE_OPTIONS_STANDARD = Object.freeze([
  { label: "Increase", value: "Increase" },
  { label: "Decrease", value: "Decrease" },
  { label: "Name Change (Principal)", value: "Name_Change_Principal" },
  { label: "Name Change (County)", value: "Name_Change_County" },
  {
    label: "Name Change (Deceased/Ward/Beneficiary)",
    value: "Name_Change_Deceased_Ward_Beneficiary",
  },
]);

/** VA admin rider selectable types (aligned with standard five options). */
export const TYPE_OPTIONS_VA = Object.freeze([
  { label: "Increase", value: "Increase" },
  { label: "Decrease", value: "Decrease" },
  { label: "Name Change (Principal)", value: "Name_Change_Principal" },
  { label: "Name Change (County)", value: "Name_Change_County" },
  {
    label: "Name Change (Deceased/Ward/Beneficiary)",
    value: "Name_Change_Deceased_Ward_Beneficiary",
  },
]);

/** Retired VA-only type — not selectable; kept for legacy endorsement log display. */
export const LEGACY_VA_ENDORSEMENT_TYPES = Object.freeze(["Name_Change_Ward"]);

export const LEGACY_VA_ENDORSEMENT_TYPE_OPTIONS = Object.freeze([
  { label: "Name Change (Ward)", value: "Name_Change_Ward" },
]);

export const NAME_CHANGE_TYPES_STANDARD = Object.freeze([
  "Name_Change_Principal",
  "Name_Change_County",
  "Name_Change_Deceased_Ward_Beneficiary",
]);

export const NAME_CHANGE_TYPES_VA = Object.freeze([
  "Name_Change_Principal",
  "Name_Change_County",
  "Name_Change_Deceased_Ward_Beneficiary",
]);

export const NAME_CHANGE_TYPES_VA_WITH_LEGACY = Object.freeze([
  ...NAME_CHANGE_TYPES_VA,
  ...LEGACY_VA_ENDORSEMENT_TYPES,
]);

export const BOND_REVIEW_FIELDS_PERSON = Object.freeze([
  { name: "firstName", label: "First Name" },
  { name: "middleName", label: "Middle Name" },
  { name: "lastName", label: "Last Name" },
  { name: "suffix", label: "Suffix" },
  { name: "aliases", label: "Aliases" },
]);

export const BOND_REVIEW_FIELDS_TRUSTEE = Object.freeze([
  { name: "legalName", label: "Legal Name" },
  { name: "trustName", label: "Trust Name" },
]);

export const RIDER_PAGE_CONFIG = {
  [RIDER_BOND_KIND.probate]: {
    bondKind: RIDER_BOND_KIND.probate,
    principalRiderMeta: {
      listKey: "personal_rep",
      idField: "pr_id",
      nameField: "name",
    },
    pageTitle: "Probate Rider",
    retrieve: (bondId) => `/retrieve-probate-bond/${bondId}/`,
    update: (bondId) => `/update_probate_bond/${bondId}/`,
    approve: (bondId) => `/approve_probate_bond/${bondId}/`,
    bondDetailPath: (bondId) => `/vermost-probate-bond-update/${bondId}`,
    nestedKey: "probatebond",
    typeOptions: TYPE_OPTIONS_STANDARD,
    nameChangeTypes: NAME_CHANGE_TYPES_STANDARD,
    bondReviewFields: BOND_REVIEW_FIELDS_PERSON,
    emptyForm: {
      firstName: "",
      lastName: "",
      middleName: "",
      suffix: "",
      aliases: "",
      bond_number: null,
      county: "",
      courtCaseNumber: "",
      bond_amount: "",
      effective_date: "",
    },
    mapBondToForm: (bondData, selectedBondNumber) => ({
      firstName: bondData.probatebond?.deceased_legal_first_name || "",
      lastName: bondData.probatebond?.deceased_legal_last_name || "",
      middleName: bondData.probatebond?.deceased_legal_middle_name || "",
      suffix: bondData.probatebond?.deceased_legal_suffix || "",
      aliases: bondData.probatebond?.alias_deceased_legal_name || "",
      bond_number: selectedBondNumber || null,
      county: bondData.county || "",
      courtCaseNumber: bondData.court_case_number || "",
      bond_amount:
        bondData.bond_amount !== undefined && bondData.bond_amount !== null
          ? bondData.bond_amount
          : "",
      effective_date: "",
    }),
    buildNestedPatch: (form, endorsementFields) => {
      const base = {
        deceased_legal_first_name: form.firstName,
        deceased_legal_middle_name: form.middleName,
        deceased_legal_last_name: form.lastName,
        deceased_legal_suffix: form.suffix,
        deceased_legal_name: buildPersonLegalName(
          form.firstName,
          form.middleName,
          form.lastName,
          form.suffix
        ),
        alias_deceased_legal_name: form.aliases,
      };
      if (
        endorsementFields?.type === "Name_Change_Deceased_Ward_Beneficiary" &&
        endorsementFields.to?.trim()
      ) {
        return { ...base, deceased_legal_name: endorsementFields.to.trim() };
      }
      return base;
    },
    patchTopLevelExtras: (form, endorsementFields, bondData, endorsementLog) => ({
      ...(form.effective_date !== undefined
        ? { effective_date: form.effective_date }
        : {}),
      ...buildPrincipalListPatchExtras(
        endorsementFields,
        bondData,
        endorsementLog,
        {
          listKey: "personal_rep",
          idField: "pr_id",
          nameField: "name",
        }
      ),
    }),
    resolvePrincipalNameLabels: (endorsementType) => {
      if (endorsementType === "Name_Change_Deceased_Ward_Beneficiary") {
        return { from: "From Deceased Name", to: "To Deceased Name" };
      }
      return { from: "From Principal", to: "To Principal" };
    },
    logCountyFetchError: true,
    logEndorsementLogError: true,
  },
  [RIDER_BOND_KIND.guardianship]: {
    bondKind: RIDER_BOND_KIND.guardianship,
    principalRiderMeta: {
      listKey: "guardians",
      idField: "guardian_id",
      nameField: "guardian_name",
    },
    pageTitle: "Guardianship Rider",
    retrieve: (bondId) => `/retrieve-guardianship-bond/${bondId}/`,
    update: (bondId) => `/update_guardianship_bond/${bondId}/`,
    approve: (bondId) => `/approve_guardianship_bond/${bondId}/`,
    bondDetailPath: (bondId) => `/vermost-guardianship-bond-update/${bondId}`,
    nestedKey: "guardianshipbond",
    typeOptions: TYPE_OPTIONS_STANDARD,
    nameChangeTypes: NAME_CHANGE_TYPES_STANDARD,
    bondReviewFields: BOND_REVIEW_FIELDS_PERSON,
    emptyForm: {
      firstName: "",
      lastName: "",
      middleName: "",
      suffix: "",
      aliases: "",
      bond_number: null,
      county: "",
      courtCaseNumber: "",
      bond_amount: "",
    },
    mapBondToForm: (bondData, selectedBondNumber) => ({
      firstName: bondData.guardianshipbond?.ward_first_name || "",
      lastName: bondData.guardianshipbond?.ward_last_name || "",
      middleName: bondData.guardianshipbond?.ward_middle_name || "",
      suffix: bondData.guardianshipbond?.ward_suffix || "",
      aliases: bondData.guardianshipbond?.ward_alias || "",
      bond_number: selectedBondNumber || null,
      county: bondData.county || "",
      courtCaseNumber: bondData.court_case_number || "",
      bond_amount: bondData.bond_amount || "",
    }),
    buildNestedPatch: (form, endorsementFields) => {
      const base = {
        ward_first_name: form.firstName,
        ward_middle_name: form.middleName,
        ward_last_name: form.lastName,
        ward_suffix: form.suffix,
        ward_legal_name: buildPersonLegalName(
          form.firstName,
          form.middleName,
          form.lastName,
          form.suffix
        ),
        ward_alias: form.aliases,
      };
      if (
        endorsementFields?.type === "Name_Change_Deceased_Ward_Beneficiary" &&
        endorsementFields.to?.trim()
      ) {
        return { ...base, ward_legal_name: endorsementFields.to.trim() };
      }
      return base;
    },
    patchTopLevelExtras: (form, endorsementFields, bondData, endorsementLog) =>
      buildPrincipalListPatchExtras(
        endorsementFields,
        bondData,
        endorsementLog,
        {
          listKey: "guardians",
          idField: "guardian_id",
          nameField: "guardian_name",
        }
      ),
    resolvePrincipalNameLabels: (endorsementType) => {
      if (endorsementType === "Name_Change_Deceased_Ward_Beneficiary") {
        return { from: "From Ward Name", to: "To Ward Name" };
      }
      return { from: "From Principal", to: "To Principal" };
    },
    logCountyFetchError: false,
    logEndorsementLogError: false,
  },
  [RIDER_BOND_KIND.trustee]: {
    bondKind: RIDER_BOND_KIND.trustee,
    principalRiderMeta: {
      listKey: "trustees",
      idField: "trustee_id",
      nameField: "name",
    },
    pageTitle: "Trustee Rider",
    retrieve: (bondId) => `/retrieve-trustee-bond/${bondId}/`,
    update: (bondId) => `/update_trustee_bond/${bondId}/`,
    approve: (bondId) => `/approve_trustee_bond/${bondId}/`,
    bondDetailPath: (bondId) => `/vermost-trustee-bond-update/${bondId}`,
    nestedKey: "trustee_bond",
    typeOptions: TYPE_OPTIONS_STANDARD,
    nameChangeTypes: NAME_CHANGE_TYPES_STANDARD,
    bondReviewFields: BOND_REVIEW_FIELDS_TRUSTEE,
    emptyForm: {
      legalName: "",
      trustName: "",
      bond_number: null,
      county: "",
      courtCaseNumber: "",
      bond_amount: "",
    },
    mapBondToForm: (bondData, selectedBondNumber) => ({
      legalName: bondData.trustee_bond?.legal_name || "",
      trustName: bondData.trustee_bond?.trust_name || "",
      bond_number: selectedBondNumber || null,
      county: bondData.county || "",
      courtCaseNumber: bondData.court_case_number || "",
      bond_amount: bondData.bond_amount || "",
    }),
    buildNestedPatch: (form, endorsementFields) => {
      const base = {
        legal_name: form.legalName,
        trust_name: form.trustName,
      };
      if (
        endorsementFields?.type === "Name_Change_Deceased_Ward_Beneficiary" &&
        endorsementFields.to?.trim()
      ) {
        return { ...base, legal_name: endorsementFields.to.trim() };
      }
      return base;
    },
    patchTopLevelExtras: (form, endorsementFields, bondData, endorsementLog) =>
      buildPrincipalListPatchExtras(
        endorsementFields,
        bondData,
        endorsementLog,
        {
          listKey: "trustees",
          idField: "trustee_id",
          nameField: "name",
        }
      ),
    resolvePrincipalNameLabels: (endorsementType) => {
      if (endorsementType === "Name_Change_Deceased_Ward_Beneficiary") {
        return { from: "From Legal Name", to: "To Legal Name" };
      }
      return { from: "From Principal", to: "To Principal" };
    },
    logCountyFetchError: false,
    logEndorsementLogError: false,
  },
  [RIDER_BOND_KIND.va]: {
    bondKind: RIDER_BOND_KIND.va,
    principalRiderMeta: {
      listKey: "principals",
      idField: "fid_id",
      nameField: "name",
    },
    pageTitle: "VA Bond Rider",
    retrieve: (bondId) => `/retrieve-va-bond/${bondId}/`,
    update: (bondId) => `/update_va_bond/${bondId}/`,
    approve: (bondId) => `/approve_va_bond/${bondId}/`,
    bondDetailPath: (bondId) => `/vermost-va-bond-update/${bondId}`,
    nestedKey: "vabond",
    typeOptions: TYPE_OPTIONS_VA,
    nameChangeTypes: NAME_CHANGE_TYPES_VA_WITH_LEGACY,
    bondReviewFields: BOND_REVIEW_FIELDS_PERSON,
    emptyForm: {
      firstName: "",
      lastName: "",
      middleName: "",
      suffix: "",
      aliases: "",
      bond_number: null,
      county: "",
      courtCaseNumber: "",
      bond_amount: "",
    },
    mapBondToForm: (bondData, selectedBondNumber) => ({
      firstName: bondData.vabond?.beneficiary_first_name || "",
      lastName: bondData.vabond?.beneficiary_last_name || "",
      middleName: bondData.vabond?.beneficiary_middle_name || "",
      suffix: bondData.vabond?.beneficiary_suffix || "",
      aliases: bondData.vabond?.beneficiary_alias || "",
      bond_number: selectedBondNumber || null,
      county: bondData.county || "",
      courtCaseNumber: bondData.court_case_number || "",
      bond_amount: bondData.bond_amount || "",
    }),
    buildNestedPatch: (form, endorsementFields) => ({
      beneficiary_first_name: form.firstName,
      beneficiary_last_name: form.lastName,
      beneficiary_middle_name: form.middleName,
      beneficiary_suffix: form.suffix,
      beneficiary_alias: form.aliases,
      beneficiary_legal_name: buildPersonLegalName(
        form.firstName,
        form.middleName,
        form.lastName,
        form.suffix
      ),
      ...((endorsementFields.type === "Name_Change_Deceased_Ward_Beneficiary" ||
        endorsementFields.type === "Name_Change_Ward") &&
      endorsementFields.to
        ? { beneficiary_legal_name: endorsementFields.to.trim() }
        : {}),
    }),
    patchTopLevelExtras: (form, endorsementFields, bondData, endorsementLog) =>
      buildPrincipalListPatchExtras(
        endorsementFields,
        bondData,
        endorsementLog,
        {
          listKey: "principals",
          idField: "fid_id",
          nameField: "name",
        }
      ),
    resolvePrincipalNameLabels: (endorsementType) => {
      if (endorsementType === "Name_Change_Ward") {
        return {
          from: "From Ward (previous name)",
          to: "To Ward (new name)",
        };
      }
      if (endorsementType === "Name_Change_Deceased_Ward_Beneficiary") {
        return { from: "From Beneficiary Name", to: "To Beneficiary Name" };
      }
      return { from: "From Principal", to: "To Principal" };
    },
    logCountyFetchError: false,
    logEndorsementLogError: false,
  },
};
